import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { fetchFaqs, Faq as FaqType } from '@/api/faq';
import { Pagination } from 'antd';

const Faq = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const initialPage = parseInt(params.get('page') || '1', 10);
  const filter = params.get('filter') || 'all';
  const keyword = (params.get('keyword') || '').toLowerCase().trim();
  const [page, setPage] = useState(initialPage);
  const pageSize = 10;

  const { data, isLoading, isError } = useQuery<FaqType[]>({
    queryKey: ['Faqs'],
    queryFn: fetchFaqs,
  });

  if (isLoading) return <StatusText>불러오는 중...</StatusText>;
  if (isError || !data) return <StatusText>데이터 로딩 실패</StatusText>;

  const filtered = data.filter((faq) => {
    const title = faq.question.toLowerCase();
    const content = faq.answer?.toLowerCase() || '';
    if (!keyword) return true;

    if (filter === 'title') return title.includes(keyword);
    if (filter === 'content') return content.includes(keyword);
    return title.includes(keyword) || content.includes(keyword);
  });

  const reversed = [...filtered];
  const totalPages = Math.ceil(reversed.length / pageSize);
  const totalItems = filtered.length;
  const pagedFaqs = reversed.slice((page - 1) * pageSize, page * pageSize);

  const formatDate = (iso: string) => {
    const date = new Date(iso);
    return date.toISOString().slice(0, 10).replace(/-/g, '.');
  };

  return (
    <>
      <Container>
        {pagedFaqs.map((faq, index) => (
          <NewsCard key={faq.id} onClick={() => navigate(`/intro/faq/${faq.id}`)}>
            <LeftBox>
              <NewsIndex>{(page - 1) * pageSize + index + 1}</NewsIndex>
            </LeftBox>
            <ContentBox>
              <NewsTitle>{faq.question}</NewsTitle>
              <NewsDate>{formatDate(faq.createdAt)}</NewsDate>
            </ContentBox>
          </NewsCard>
        ))}
      </Container>
      <PaginationWrapper>
        {totalPages > 1 && (
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalItems}
            onChange={(newPage) => setPage(newPage)}
            showSizeChanger={false} // 페이지 크기 선택 비활성화
          />
        )}
      </PaginationWrapper>
    </>
  );
};

export default Faq;

// ✅ styled-components

const Container = styled.div`
  display: flex;
  width: 100%;
  max-width: 850px;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 16px;
  box-sizing: border-box;
`;

const NewsCard = styled.div`
  display: flex;
  padding: 16px 12px;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  flex-wrap: wrap;
  cursor: pointer;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 8px;
  }

  &:hover {
    background-color: #f0f3fe;
    color: #0b46dd;
  }
`;

const LeftBox = styled.div`
  display: flex;
  width: 56px;
  padding-right: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    padding-right: 0;
    width: auto;
  }
`;

const NewsIndex = styled.div`
  color: var(--gray-ui_gray05, #999);
  font-size: 18px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContentBox = styled.div`
  flex: 1 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: start;
  width: 100%;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 4px;
  }
`;

const NewsTitle = styled.div`
  flex: 1;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 18px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.36px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    white-space: normal;
    font-size: 16px;
  }
`;

const NewsDate = styled.div`
  color: var(--gray-ui_gray04, #6f6f6f);
  text-align: right;
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.28px;
  margin-left: 12px;

  @media (max-width: 768px) {
    margin-left: 0;
    font-size: 13px;
  }
`;

const StatusText = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 0;
  font-size: 16px;
  color: #999;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const PaginationWrapper = styled.div`
  margin-top: 40px;
  margin-bottom: 40px;
  min-height: 40px;
  display: flex;
  justify-content: center;
`;
